import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Type, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@coin/shared/core/util-environments';
import { DownloadFileHelper } from '@coin/shared/core/util-helpers';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { LoadingService } from './loading/loading.service';
import { RequestHandlerService } from './request-handler.service';

@Injectable({
  providedIn: 'root'
})
export class AttachmentService {
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  private requestHandler = inject(RequestHandlerService);
  private toastrService = inject(ToastrService);
  private loadingService = inject(LoadingService);

  public upload(handler: string, file: File, key: string, withId = true): Observable<{ signedUrl: string }> {
    const params = new HttpParams().append('key', key).append('withId', withId).append('size', file.size);
    return this.http
      .get<{
        signedUrl: string;
      }>(`${environment.api.documentsUrl}/${handler}/upload`, { params })
      .pipe(
        switchMap(({ signedUrl }) => this.http.put(signedUrl, file).pipe(map(() => ({ signedUrl })))),
        this.requestHandler.handleError(`Error while saving the file: ${key}`, true)
      );
  }

  // TODO: remove dialog opening from service
  public download(filename: string, handler: string, confirmationDialogComponent: Type<unknown>): Observable<void> {
    return this.http
      .post(
        `${environment.api.documentsUrl}/${handler}/download?signedUrl=true`,
        {
          key: filename
        },
        { responseType: 'text' }
      )
      .pipe(
        switchMap(response => {
          return this.dialog
            .open(confirmationDialogComponent, {
              data: {
                headline: 'general.download-ready',
                msg: 'general.download-ready-info-message',
                cancelMsg: 'general.btnCancel',
                confirmMsg: 'general.btnDownload',
                translate: true
              }
            })
            .afterClosed()
            .pipe(
              filter(confirmation => {
                if (confirmation) {
                  return true;
                }
                this.toastrService.warning('Cancelled Download');
                return false;
              }),
              switchMap(() => of(response))
            );
        }),
        map(response => DownloadFileHelper.downloadUrl(this.http, response as string, filename.split('/').pop())),
        tap(() => this.toastrService.success('Downloaded File')),
        this.requestHandler.handleError(`Error while loading the file: ${filename}`, true)
      )
      .pipe(this.loadingService.withLoadingScreen);
  }

  public isFileAvailable(handler: string, key: string): Observable<boolean> {
    return this.http
      .get<{ key: string }[]>(`${environment.api.documentsUrl}/${handler}/list/info`, { params: { prefix: key } })
      .pipe(
        map(files => files.some(f => f.key.endsWith(key))),
        catchError(() => of(false))
      );
  }

  public deleteFile(handler: string, key: string) {
    return this.http.delete(`${environment.api.documentsUrl}/${handler}`, { params: { key } });
  }
}
